import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ExecutionPageService,
  type ExecutionEscalationPageQuery,
  type ExecutionEscalationSignal,
  type ExecutionEventsPageQuery,
  type ExecutionEventRow,
  type ExecutionInstallPageQuery,
  type ExecutionInstallRow,
  type ExecutionInstallSummary,
  type ExecutionSignalClusterRow,
  type PageResponse,
} from "@/services/execution-page.service";

function emptyPage<T>(): PageResponse<T> {
  return { data: [], count: 0, error: null };
}

export function useExecutionInstallRowsPage(query: ExecutionInstallPageQuery & { enabled?: boolean }) {
  const [state, setState] = useState<PageResponse<ExecutionInstallRow>>(emptyPage());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!query.enabled) {
      setState(emptyPage());
      setLoading(false);
      return () => { cancelled = true; };
    }

    setLoading(true);
    void ExecutionPageService.getInstallRowsPage(query).then((result) => {
      if (cancelled) return;
      setState(result);
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [query.enabled, query.page, query.pageSize, query.search, query.lifecycle, query.refreshToken]);

  return { ...state, loading };
}

export function useExecutionEscalationsPage(query: ExecutionEscalationPageQuery & { enabled?: boolean }) {
  const [state, setState] = useState<PageResponse<ExecutionEscalationSignal>>(emptyPage());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!query.enabled) {
      setState(emptyPage());
      setLoading(false);
      return () => { cancelled = true; };
    }

    setLoading(true);
    void ExecutionPageService.getEscalationsPage(query).then((result) => {
      if (cancelled) return;
      setState(result);
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [query.enabled, query.page, query.pageSize, query.refreshToken]);

  return { ...state, loading };
}

export function useExecutionSummary(query: { enabled?: boolean; refreshToken?: number }) {
  const [data, setData] = useState<ExecutionInstallSummary | null>(null);
  const [error, setError] = useState<unknown | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!query.enabled) {
      setData(null);
      setError(null);
      setLoading(false);
      return () => { cancelled = true; };
    }

    setLoading(true);
    void ExecutionPageService.getSummary(query.refreshToken).then((result) => {
      if (cancelled) return;
      setData(result.data);
      setError(result.error);
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [query.enabled, query.refreshToken]);

  return { data, error, loading };
}

export function useExecutionEventsPage(query: ExecutionEventsPageQuery & { enabled?: boolean }) {
  const [state, setState] = useState<PageResponse<ExecutionEventRow>>(emptyPage());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!query.enabled) {
      setState(emptyPage());
      setLoading(false);
      return () => { cancelled = true; };
    }

    setLoading(true);
    void ExecutionPageService.getEventsPage(query).then((result) => {
      if (cancelled) return;
      setState(result);
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [query.enabled, query.page, query.pageSize, query.filter, query.refreshToken]);

  return { ...state, loading };
}

export function useExecutionClusters(query: { enabled?: boolean; refreshToken?: number }) {
  const [data, setData] = useState<ExecutionSignalClusterRow[]>([]);
  const [error, setError] = useState<unknown | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!query.enabled) {
      setData([]);
      setError(null);
      setLoading(false);
      return () => { cancelled = true; };
    }

    setLoading(true);
    void ExecutionPageService.getClusters(query.refreshToken).then((result) => {
      if (cancelled) return;
      setData(result.data);
      setError(result.error);
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, [query.enabled, query.refreshToken]);

  return { data, error, loading };
}

export function useExecutionServerData(query: {
  enabled?: boolean;
  installPage?: number;
  installPageSize?: number;
  search?: string;
  lifecycle?: ExecutionInstallPageQuery["lifecycle"];
  escalationPage?: number;
  escalationPageSize?: number;
}) {
  const [refreshToken, setRefreshToken] = useState(0);
  const rowsPage = useExecutionInstallRowsPage({
    enabled: query.enabled,
    page: query.installPage ?? 0,
    pageSize: query.installPageSize ?? 25,
    search: query.search,
    lifecycle: query.lifecycle,
    refreshToken,
  });
  const escalationsPage = useExecutionEscalationsPage({
    enabled: query.enabled,
    page: query.escalationPage ?? 0,
    pageSize: query.escalationPageSize ?? 10,
    refreshToken,
  });
  const summary = useExecutionSummary({ enabled: query.enabled, refreshToken });

  const rows = useMemo(() => rowsPage.data, [rowsPage.data]);
  const escalations = useMemo(() => escalationsPage.data, [escalationsPage.data]);
  const refresh = useCallback(() => {
    setRefreshToken((value) => value + 1);
  }, []);

  return {
    rows,
    rowsCount: rowsPage.count,
    escalations,
    escalationsCount: escalationsPage.count,
    error: rowsPage.error ?? escalationsPage.error ?? summary.error,
    loading: rowsPage.loading || escalationsPage.loading || summary.loading,
    summary: summary.data,
    refresh,
  };
}
